// src/features/diagnostics/diagnosticsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { agent } from "../../../utils/agent";
import * as API from "../../../utils/apiPath";

// Async thunk to fetch objective questions
export const fetchObjQuestions = createAsyncThunk(
    "diagnostics/fetchObjQuestions",
    async (userId, { rejectWithValue }) => {
        try {
            const response = await agent.get(`${API.GET_OBJ_QUEST}/${userId}`);
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const fetchMyMentors = createAsyncThunk(
    "diagnostics/fetchMyMentors",
    async (userId, request, { rejectWithValue }) => {
        try {
            const response = await agent.get(`${API.LOAD_MY_MENTORS}/${userId}`, {
                params: request,
            });
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

// Async thunk to check diagnostic toolkit status
export const fetchCheckDiagnosticToolkit = createAsyncThunk(
    "diagnostics/fetchCheckDiagnosticToolkit",
    async (userId, { rejectWithValue }) => {
        try {
            const response = await agent.post(API.CHECK_DIAGNOSTIC, { user_id: userId });
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

// Async thunk to submit diagnostic answers
export const submitDiagnostics = createAsyncThunk(
    "diagnostics/submitDiagnostics",
    async ({ data, actionType }, { rejectWithValue }) => {
        try {
            const payload = { ...data, action: actionType };
            const response = await agent.post(API.SET_OBJ_QUEST, payload);
            return response.data;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

const myDiagnosticToolkitSlice = createSlice({
    name: "myDiagnosticToolkit",
    initialState: {
        objQuestions: [],
        checkDiagnosticToolkit: {},
        loading: false,
        shareLoading: false,
        submitStatus: null,
        error: null,
    },
    reducers: {
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setShareLoading(state, action) {
            state.shareLoading = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch questions
            .addCase(fetchObjQuestions.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchObjQuestions.fulfilled, (state, action) => {
                state.objQuestions = action.payload;
                state.loading = false;
            })
            .addCase(fetchObjQuestions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchMyMentors.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchMyMentors.fulfilled, (state, action) => {
                state.mentors = action.payload;
                state.loading = false;
            })
            .addCase(fetchMyMentors.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Fetch diagnostic toolkit status
            .addCase(fetchCheckDiagnosticToolkit.fulfilled, (state, action) => {
                state.checkDiagnosticToolkit = action.payload;
            })

            // Submit diagnostics
            .addCase(submitDiagnostics.pending, (state, action) => {
                if (action.meta.arg.actionType === "save") state.loading = true;
                else state.shareLoading = true;
                state.submitStatus = null;
            })
            .addCase(submitDiagnostics.fulfilled, (state, action) => {
                state.loading = false;
                state.shareLoading = false;
                state.submitStatus = action.payload;
            })
            .addCase(submitDiagnostics.rejected, (state, action) => {
                state.loading = false;
                state.shareLoading = false;
                state.error = action.payload;
            });
    },
});

export const { setLoading, setShareLoading } = myDiagnosticToolkitSlice.actions;
export default myDiagnosticToolkitSlice.reducer;
